import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { PlatformCard } from "@/components/PlatformCard";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Play,
  Link as LinkIcon,
  Eye,
  Calendar,
} from "lucide-react";
import type { Stream, Speaker } from "@shared/schema";

export default function Dashboard() {
  const { data: streams = [], isLoading } = useQuery<Stream[]>({
    queryKey: ["/api/streams"],
  });

  const { data: speakers = [] } = useQuery<Speaker[]>({
    queryKey: ["/api/speakers"],
  });

  // Calculate real statistics from streams data
  const stats = useMemo(() => {
    const activeStreams = streams.filter((s: any) => s.status === "live");
    const scheduledStreams = streams.filter((s: any) => s.status === "scheduled");
    
    // Get unique platforms across all streams
    const platformsSet = new Set<string>();
    streams.forEach((stream: any) => {
      const platforms = stream.platforms as any[] || [];
      platforms.forEach((p: any) => platformsSet.add(p.name));
    });
    
    // Get next scheduled stream
    const upcomingStreams = scheduledStreams
      .map((s: any) => ({
        ...s,
        dateTime: new Date(`${s.date}T${s.time}`),
      }))
      .filter((s: any) => s.dateTime > new Date())
      .sort((a: any, b: any) => a.dateTime.getTime() - b.dateTime.getTime());
    
    const nextStream = upcomingStreams[0];
    const nextStreamDate = nextStream 
      ? new Date(`${nextStream.date}T${nextStream.time}`)
      : null;
    
    const nextStreamText = nextStreamDate
      ? nextStreamDate.toLocaleString("en-US", {
          weekday: "short",
          hour: "numeric",
          minute: "2-digit",
        })
      : "None scheduled";
    
    const nextStreamDesc = nextStream?.title || "No upcoming streams";
    
    // Count active platforms
    const activePlatformNames = activeStreams
      .flatMap((s: any) => (s.platforms as any[] || []).map((p: any) => p.name))
      .filter((name: string, index: number, arr: string[]) => arr.indexOf(name) === index)
      .map((name: string) => {
        if (name === "youtube") return "YT";
        if (name === "facebook") return "FB";
        if (name === "x") return "X";
        if (name === "twitch") return "TW";
        return name.toUpperCase().slice(0, 2);
      })
      .join(", ") || "None";

    return {
      activeCount: activeStreams.length,
      platformCount: platformsSet.size,
      platformNames: activePlatformNames,
      nextStreamTime: nextStreamText,
      nextStreamTitle: nextStreamDesc,
    };
  }, [streams]);

  const statsCards = [
    {
      title: "Active Streams",
      value: stats.activeCount.toString(),
      description: stats.activeCount > 0 ? stats.platformNames : "No active streams",
      icon: Play,
      color: "text-accent",
      bgColor: "bg-accent/10",
    },
    {
      title: "Platforms Connected",
      value: stats.platformCount.toString(),
      description: stats.platformCount > 0 ? "Available for streaming" : "Configure in streams",
      icon: LinkIcon,
      color: "text-secondary",
      bgColor: "bg-secondary/10",
    },
    {
      title: "Total Streams",
      value: streams.length.toString(),
      description: "All time",
      icon: Eye,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      title: "Next Service",
      value: stats.nextStreamTime,
      description: stats.nextStreamTitle,
      icon: Calendar,
      color: "text-muted-foreground",
      bgColor: "bg-muted",
    },
  ];

  // Generate platform cards from actual stream data
  const platformLogos = {
    youtube: (
      <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    ),
    facebook: (
      <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
    x: (
      <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
    twitch: (
      <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
      </svg>
    ),
  };

  const platformConfig = {
    youtube: { displayName: "YouTube Live", color: "bg-red-600", textColor: "text-red-700", bgColor: "bg-red-100" },
    facebook: { displayName: "Facebook Live", color: "bg-blue-600", textColor: "text-blue-700", bgColor: "bg-blue-100" },
    x: { displayName: "X (formerly Twitter)", color: "bg-black", textColor: "text-gray-700", bgColor: "bg-gray-100" },
    twitch: { displayName: "Twitch", color: "bg-purple-600", textColor: "text-purple-700", bgColor: "bg-purple-100" },
  };

  const platforms = useMemo(() => {
    // Find streams with each platform
    const platformStreams: Record<string, any[]> = {
      youtube: [],
      facebook: [],
      x: [],
      twitch: [],
    };

    streams.forEach((stream: any) => {
      const streamPlatforms = stream.platforms as any[] || [];
      streamPlatforms.forEach((p: any) => {
        if (platformStreams[p.name]) {
          platformStreams[p.name].push({ ...stream, platformConfig: p });
        }
      });
    });

    // Create platform cards
    return Object.entries(platformConfig).map(([key, config]) => {
      const streamsForPlatform = platformStreams[key];
      const liveStream = streamsForPlatform.find((s: any) => s.status === "live");
      const scheduledStream = streamsForPlatform.find((s: any) => s.status === "scheduled");
      const anyStream = liveStream || scheduledStream || streamsForPlatform[0];

      let status = "inactive";
      let title = "No stream configured";
      let additionalInfo: any = {};

      if (liveStream) {
        status = "live";
        title = liveStream.title;
        additionalInfo = {
          viewers: "—",
          quality: "1080p",
          visibility: "Public",
        };
      } else if (scheduledStream) {
        status = "scheduled";
        title = scheduledStream.title;
        const scheduledDate = new Date(`${scheduledStream.date}T${scheduledStream.time}`);
        additionalInfo = {
          scheduledTime: scheduledDate.toLocaleString("en-US", {
            weekday: "short",
            hour: "numeric",
            minute: "2-digit",
          }),
          quality: "720p",
          visibility: "Ready",
        };
      } else if (anyStream) {
        status = "inactive";
        title = anyStream.title;
        additionalInfo = {
          quality: "720p",
        };
      }

      return {
        name: config.displayName,
        status,
        title,
        ...additionalInfo,
        color: config.color,
        textColor: config.textColor,
        bgColor: config.bgColor,
        logo: platformLogos[key as keyof typeof platformLogos],
      };
    });
  }, [streams]);

  // Map streams from database to display format
  const recentStreams = streams.map((stream) => {
    const speaker = speakers.find((s) => s.id === stream.speakerId);
    const speakerName = speaker ? `${speaker.firstName} ${speaker.lastName}` : "Unknown Speaker";
    
    // Map platforms to short codes
    const platformsArray = (stream.selectedPlatforms as any) || [];
    const platformCodes = platformsArray.map((p: any) => {
      const platformId = typeof p === "string" ? p : p.name;
      if (platformId === "youtube") return "YT";
      if (platformId === "facebook") return "FB";
      if (platformId === "x") return "X";
      if (platformId === "twitch") return "TW";
      return platformId.toUpperCase().slice(0, 2);
    });

    // Format date
    const date = new Date(stream.date);
    const formattedDate = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    // Status formatting
    let statusText = stream.status || "draft";
    let statusColor = "bg-muted text-muted-foreground";
    if (statusText === "live") {
      statusText = "Live Now";
      statusColor = "bg-accent/10 text-accent";
    } else if (statusText === "scheduled") {
      statusText = "Scheduled";
      statusColor = "bg-blue-100 text-blue-700";
    } else if (statusText === "completed") {
      statusText = "Completed";
      statusColor = "bg-muted text-muted-foreground";
    } else if (statusText === "draft") {
      statusText = "Draft";
      statusColor = "bg-yellow-100 text-yellow-700";
    }

    return {
      id: stream.id,
      title: stream.title,
      speaker: speakerName,
      date: formattedDate,
      platforms: platformCodes,
      peakViewers: stream.peakViewers || 0,
      status: statusText,
      statusColor,
    };
  });

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6 lg:mb-8">
        {statsCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs lg:text-sm font-medium text-muted-foreground">{stat.title}</h3>
                  <div className={`w-7 h-7 lg:w-8 lg:h-8 ${stat.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 lg:w-5 lg:h-5 ${stat.color}`} />
                  </div>
                </div>
                <p className="text-2xl lg:text-3xl font-bold truncate" data-testid={`text-${stat.title.toLowerCase().replace(/\s+/g, '-')}-value`}>
                  {stat.value}
                </p>
                <p className="text-xs text-muted-foreground mt-1 truncate">{stat.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Platform Cards */}
      <div className="mb-6 lg:mb-8" data-tour="platforms-section">
        <h3 className="text-base lg:text-lg font-semibold mb-3 lg:mb-4">Connected Platforms</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          {platforms.map((platform) => (
            <PlatformCard key={platform.name} platform={platform} />
          ))}
        </div>
      </div>

      {/* Recent Streams */}
      <div>
        <div className="flex items-center justify-between mb-3 lg:mb-4">
          <h3 className="text-base lg:text-lg font-semibold">Recent Streams</h3>
          <button className="text-xs lg:text-sm text-primary hover:text-primary/80 font-medium" data-testid="button-view-all">
            <span className="hidden sm:inline">View All →</span>
            <span className="sm:hidden">All →</span>
          </button>
        </div>
        <Card className="overflow-hidden">
          {/* Desktop/Tablet Table View */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stream</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Platforms</TableHead>
                  <TableHead className="text-right">Peak Viewers</TableHead>
                  <TableHead className="text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentStreams.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8 text-sm">
                      No streams yet. Create your first stream to get started!
                    </TableCell>
                  </TableRow>
                ) : (
                  recentStreams.map((stream: any) => (
                    <TableRow key={stream.id} className="hover:bg-muted/30" data-testid={`row-stream-${stream.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-base truncate max-w-[300px]">{stream.title}</p>
                          <p className="text-sm text-muted-foreground truncate max-w-[250px]">{stream.speaker}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{stream.date}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {stream.platforms.map((platform: string) => (
                            <Badge key={platform} variant="secondary" className="text-xs">
                              {platform}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-sm text-right">{stream.peakViewers}</TableCell>
                      <TableCell className="text-right">
                        <Badge className={`text-xs ${stream.statusColor} rounded-full font-medium whitespace-nowrap`}>
                          {stream.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-border">
            {recentStreams.length === 0 ? (
              <div className="text-center text-muted-foreground py-8 text-sm px-4">
                No streams yet. Create your first stream to get started!
              </div>
            ) : (
              recentStreams.map((stream: any) => (
                <div key={stream.id} className="p-4 hover:bg-muted/30" data-testid={`row-stream-${stream.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm truncate">{stream.title}</h4>
                      <p className="text-xs text-muted-foreground truncate">{stream.speaker}</p>
                    </div>
                    <Badge className={`text-xs ${stream.statusColor} rounded-full font-medium whitespace-nowrap flex-shrink-0`}>
                      {stream.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {stream.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {stream.peakViewers}
                    </span>
                  </div>
                  <div className="flex gap-1 flex-wrap mt-2">
                    {stream.platforms.map((platform: string) => (
                      <Badge key={platform} variant="secondary" className="text-xs">
                        {platform}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
